import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Check, Smartphone, ShieldOff } from "lucide-react";
import AuthPageLayout from "./AuthPageLayout";
import LoginDownloadPanel from "./LoginDownloadPanel";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const inputClass =
  "w-full px-4 py-3.5 rounded-[12px] border border-[#E0E3EF] bg-white outline-none text-cm-navy text-[14px] focus:border-cm-blue focus:ring-2 focus:ring-cm-blue/10 transition disabled:bg-cm-surface";

function buildSteps(_otpRequired) {
  // Email ownership OTP is always required; admin flag only toggles mobile OTP.
  return [
    { k: 1, label: "Account" },
    { k: 2, label: "Email" },
    { k: 3, label: "Mobile" },
  ];
}

function Stepper({ current, steps }) {
  return (
    <div className="flex items-center gap-1.5 mb-8 overflow-x-auto cm-no-scrollbar">
      {steps.map((s, i) => {
        const done = s.k < current;
        const active = s.k === current;
        return (
          <div key={s.k} className="flex items-center gap-1.5 shrink-0">
            <div
              className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold transition
                ${done ? "bg-cm-blue text-white" : active ? "bg-cm-blue text-white ring-4 ring-cm-blue/15" : "bg-[#EEF1F7] text-cm-grey"}`}
              data-testid={`step-indicator-${s.k}`}
            >
              {done ? <Check size={13} /> : s.k}
            </div>
            <div className={`text-[11px] font-medium ${active ? "text-cm-navy" : "text-cm-grey"}`}>
              {s.label}
            </div>
            {i < steps.length - 1 && (
              <div className={`h-[2px] w-6 rounded ${done ? "bg-cm-blue" : "bg-[#EEF1F7]"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Input({ label, hint, error, testid, right, ...props }) {
  return (
    <div>
      {label && <label className="text-[13px] font-semibold text-cm-navy">{label}</label>}
      <div className="relative mt-1.5">
        <input
          {...props}
          data-testid={testid}
          className={`${inputClass} ${error ? "border-red-400 focus:border-red-400 focus:ring-red-400/10" : ""} ${
            right ? "pr-11" : ""
          } ${props.className || ""}`}
        />
        {right}
      </div>
      {hint && <p className="mt-1 text-[12px] text-cm-grey">{hint}</p>}
      {error && <p className="mt-1 text-[12px] text-red-600">{error}</p>}
    </div>
  );
}

function PrimaryBtn({ loading, children, testid, ...props }) {
  return (
    <button
      type="submit"
      disabled={loading || props.disabled}
      data-testid={testid}
      className="w-full mt-2 py-3.5 rounded-[12px] bg-cm-blue hover:bg-cm-blue-deep transition text-white font-bold text-[15px] disabled:opacity-60 flex items-center justify-center gap-2 shadow-[0_12px_32px_-14px_rgba(2,44,237,0.55)]"
      {...props}
    >
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </button>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div data-testid="register-error"
         className="text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-[12px] px-4 py-3">
      {msg}
    </div>
  );
}

/* ===================== Verify Later Popup ===================== */
function VerifyLaterPopup({ onContinue }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-[20px] border border-[var(--cm-border)] shadow-2xl max-w-[420px] w-full p-6">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
            <ShieldOff size={32} className="text-amber-500" />
          </div>
          <div>
            <h3 className="text-[18px] font-extrabold text-cm-navy mb-2">Mobile verification skipped</h3>
            <p className="text-[13px] text-cm-grey leading-relaxed">
              Your phone number has been saved but is not yet verified.
              You can verify it anytime from your{" "}
              <strong className="text-cm-navy">Profile</strong> page — it only takes a minute.
            </p>
          </div>
          <div className="w-full space-y-2">
            <div className="flex items-center gap-3 p-3 rounded-[10px] bg-cm-surface border border-[var(--cm-border)]">
              <Smartphone size={16} className="text-cm-grey shrink-0" />
              <p className="text-[12px] text-cm-grey text-left">
                Mobile verification: go to <strong>Profile → Verify Mobile</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onContinue}
            className="w-full py-3.5 rounded-[12px] bg-cm-blue text-white font-bold text-[15px] hover:bg-cm-blue-deep transition"
            data-testid="verify-later-continue"
          >
            Continue to app
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================== Step 3: Mobile OTP =========================== */
function Step3Mobile({ onDone, otpRequired }) {
  const [phase, setPhase] = useState("enter");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function sendOtp(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      if (otpRequired) {
        const { data } = await api.post("/api/auth/mobile/send-otp", { mobile_number: mobile });
        setDevOtp(data?.dev_mobile_otp || "");
        setPhase("otp");
      } else {
        await api.post("/api/auth/mobile/save-number", { mobile_number: mobile });
        onDone({ skippedOtp: true });
      }
    }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  async function verifyOtp(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await api.post("/api/auth/mobile/verify-otp", { code }); onDone({ skippedOtp: false }); }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  if (phase === "otp" && otpRequired) {
    return (
      <form onSubmit={verifyOtp} className="space-y-5" data-testid="step3-form-otp">
        <h2 className="text-[22px] font-extrabold text-cm-navy tracking-tight">Verify your mobile</h2>
        <p className="text-cm-grey text-[14px] -mt-2">
          Enter the 6-digit code for <span className="font-semibold text-cm-navy">{mobile}</span>.
        </p>
        <OtpDevHint
          label="Mobile"
          code={devOtp}
          note={devOtp ? "Dev mode — use the code above (SMS provider disabled)." : null}
        />
        <Input label="Mobile OTP" inputMode="numeric" maxLength={6} required
               value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
               testid="step3-code" placeholder="______"
               className="text-center tracking-[0.4em] font-mono text-[18px]" />
        <ErrorBox msg={err} />
        <PrimaryBtn loading={loading} testid="step3-submit">Verify mobile</PrimaryBtn>
        <button type="button" onClick={() => setPhase("enter")}
                className="w-full text-[13px] text-cm-grey hover:underline">
          ← Change number
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={sendOtp} className="space-y-5" data-testid="step3-form">
      <h2 className="text-[22px] font-extrabold text-cm-navy tracking-tight">
        {otpRequired ? "Verify your mobile" : "Add your mobile number"}
      </h2>
      <p className="text-cm-grey text-[14px] -mt-2">
        {otpRequired
          ? "We need your mobile number for security alerts (required by FIU-IND)."
          : "Add your mobile number. You can verify it later from your profile."}
      </p>
      {!otpRequired && (
        <div className="flex items-start gap-3 p-3 rounded-[10px] bg-amber-50 border border-amber-100">
          <ShieldOff size={16} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-[12px] text-amber-800">
            OTP verification is currently disabled by your administrator. Your number will be saved but not verified until you verify it from your profile.
          </p>
        </div>
      )}
      <Input label="Mobile number (India)" type="tel" required value={mobile}
             onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ""))}
             testid="step3-mobile" placeholder="10-digit number" maxLength={13} />
      <ErrorBox msg={err} />
      <PrimaryBtn loading={loading} testid="step3-send-otp">
        {otpRequired ? "Send OTP" : "Save number & continue"}
      </PrimaryBtn>
    </form>
  );
}

function Step1Account({ onDone }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [consents, setConsents] = useState({ terms: false, privacy: false, aml: false, risk: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [emailAvail, setEmailAvail] = useState(null); // null | checking | available | taken

  useEffect(() => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !normalized.includes("@") || !normalized.includes(".")) {
      setEmailAvail(null);
      return;
    }
    setEmailAvail("checking");
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/api/auth/check-email", { params: { email: normalized } });
        setEmailAvail(data.available ? "available" : "taken");
      } catch {
        setEmailAvail(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [email]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const name = fullName.trim().replace(/\s+/g, " ");
    if (name.length < 2 || name === "-") return setErr("Please enter your full name");
    if (password !== confirm) return setErr("Passwords do not match");
    if (!Object.values(consents).every(Boolean))
      return setErr("Please accept all terms to continue");
    const normalized = email.trim().toLowerCase();
    if (emailAvail === "taken") {
      return setErr("This email is already registered. Try signing in instead.");
    }
    setLoading(true);
    try {
      if (emailAvail !== "available") {
        const { data: check } = await api.get("/api/auth/check-email", { params: { email: normalized } });
        if (!check.available) {
          setEmailAvail("taken");
          return setErr("This email is already registered. Try signing in instead.");
        }
        setEmailAvail("available");
      }
      const { data } = await api.post("/api/auth/register", {
        email: normalized,
        full_name: name,
        password,
        confirm_password: confirm,
        accept_terms: consents.terms,
        accept_privacy: consents.privacy,
        accept_aml: consents.aml,
        accept_risk: consents.risk,
      });
      onDone({
        devOtp: data.dev_email_otp,
        nextStep: data.next_step,
        otpRequired: data.otp_required !== false,
        email,
      });
    } catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="step1-form">
      <h2 className="text-[22px] font-extrabold text-cm-navy tracking-tight">Create your account</h2>
      <p className="text-cm-grey text-[14px] -mt-2">
        Start trading with a verified CoinMBX account.
      </p>

      <Input label="Full name" type="text" required value={fullName}
             onChange={(e) => setFullName(e.target.value)}
             testid="step1-full-name" placeholder="As on your government ID" autoComplete="name" />

      <Input label="Email address" type="email" required value={email}
             onChange={(e) => setEmail(e.target.value)}
             testid="step1-email" placeholder="you@example.com" autoComplete="email"
             error={
               emailAvail === "taken"
                 ? "This email is already registered"
                 : undefined
             }
             hint={
               emailAvail === "checking"
                 ? "Checking availability…"
                 : emailAvail === "available"
                   ? "Email is available"
                   : undefined
             } />

      <Input label="Password" type={showPw ? "text" : "password"} required value={password}
             onChange={(e) => setPassword(e.target.value)}
             testid="step1-password" placeholder="Min 8 chars • Aa1!" autoComplete="new-password"
             right={
               <button type="button" onClick={() => setShowPw((v) => !v)}
                       className="absolute right-3 top-1/2 -translate-y-1/2 text-cm-grey p-1"
                       aria-label="Toggle password">
                 {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
               </button>
             }
             hint="At least 8 characters with uppercase, lowercase, number and a special character." />

      <Input label="Confirm password" type={showPw ? "text" : "password"} required value={confirm}
             onChange={(e) => setConfirm(e.target.value)}
             testid="step1-confirm" placeholder="Re-enter password" autoComplete="new-password" />

      <div className="space-y-2.5 pt-1">
        {[
          { k: "terms", label: "I accept the Terms of Service" },
          { k: "privacy", label: "I accept the Privacy Policy" },
          { k: "aml", label: "I accept the AML/KYC Policy" },
          { k: "risk", label: "I accept the Risk Disclosure" },
        ].map((c) => (
          <label key={c.k} className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={consents[c.k]}
                   onChange={(e) => setConsents({ ...consents, [c.k]: e.target.checked })}
                   data-testid={`step1-consent-${c.k}`}
                   className="mt-1 w-4 h-4 rounded accent-[#022CED]" />
            <span className="text-[13px] text-cm-grey leading-relaxed">{c.label}</span>
          </label>
        ))}
      </div>

      <ErrorBox msg={err} />
      <PrimaryBtn loading={loading} testid="step1-submit">Continue</PrimaryBtn>

      <p className="text-center text-[13px] text-cm-grey">
        Already have an account?{" "}
        <Link to="/login" className="text-cm-blue font-bold hover:underline" data-testid="register-to-login">
          Sign in
        </Link>
      </p>
    </form>
  );
}

function OtpDevHint({ label, code, note }) {
  if (!code) return null;
  return (
    <div
      className="text-[12px] bg-amber-50 text-amber-800 border border-amber-200 rounded-[10px] px-4 py-3"
      data-testid="otp-dev-hint"
    >
      <strong>{label} verification code:</strong>{" "}
      <span className="font-mono text-[14px]">{code}</span>
      {note ? <span className="block mt-1 text-[11px] text-amber-700/90">{note}</span> : null}
    </div>
  );
}

/* =========================== Step 2: Email OTP =========================== */
function Step2Email({ onDone, onCancelSignup, email, initialDevOtp, onDevOtp }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState(initialDevOtp || "");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  async function verify(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await api.post("/api/auth/email/verify-otp", { code }); onDone(); }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  async function resend() {
    setResending(true);
    try {
      const { data } = await api.post("/api/auth/email/resend-otp");
      setCooldown(60);
      if (data?.dev_email_otp) {
        setDevOtp(data.dev_email_otp);
        onDevOtp?.(data.dev_email_otp);
      }
    }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setResending(false); }
  }

  return (
    <form onSubmit={verify} className="space-y-5" data-testid="step2-form">
      <h2 className="text-[22px] font-extrabold text-cm-navy tracking-tight">Verify your email</h2>
      <p className="text-cm-grey text-[14px] -mt-2">
        We sent a 6-digit code to <span className="font-semibold text-cm-navy">{email}</span>.{" "}
        <button
          type="button"
          onClick={onCancelSignup}
          data-testid="step2-change-email"
          className="text-cm-blue font-semibold hover:underline"
        >
          Change email
        </button>
      </p>

      <OtpDevHint
        label="Email"
        code={devOtp}
        note="Email is not sent via SMTP yet — use the code above (often 123456 until mail is configured)."
      />
      {!devOtp ? (
        <p className="text-[12px] text-cm-grey bg-cm-surface border border-[var(--cm-border)] rounded-[10px] px-4 py-3">
          Check your inbox when email delivery is enabled. Until then, resend may surface a code in the yellow box above.
        </p>
      ) : null}

      <Input label="Email verification code" inputMode="numeric" maxLength={6} required
             value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
             testid="step2-code" placeholder="______"
             className="text-center tracking-[0.4em] font-mono text-[18px]" />

      <ErrorBox msg={err} />

      <div className="flex items-center justify-between text-[13px]">
        <button type="button" onClick={resend} disabled={resending || cooldown > 0}
                data-testid="step2-resend"
                className="text-cm-blue font-semibold hover:underline disabled:opacity-50 disabled:no-underline">
          {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending…" : "Resend code"}
        </button>
      </div>

      <PrimaryBtn loading={loading} testid="step2-submit">Verify email</PrimaryBtn>
    </form>
  );
}

/* =========================== Container =========================== */
export default function Register() {
  const nav = useNavigate();
  const { user, pendingUser, fetchMe, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [otpRequired, setOtpRequired] = useState(true);
  const [devEmailOtp, setDevEmailOtp] = useState("");
  const [showVerifyLater, setShowVerifyLater] = useState(false);
  const completingRef = useRef(false);

  useEffect(() => {
    api.get("/api/auth/signup-config")
      .then(({ data }) => setOtpRequired(data.otp_verification_required !== false))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (completingRef.current) return;
    if (user) {
      nav("/markets", { replace: true });
      return;
    }
    if (pendingUser === null) {
      setStep(1);
      return;
    }
    if (!pendingUser.email_verified) setStep(2);
    else if (otpRequired ? !pendingUser.mobile_verified : true) setStep(3);
  }, [user, pendingUser, nav, otpRequired]);

  async function refreshThen(next) {
    await fetchMe();
    setStep(next);
  }

  async function enterAppAfterSignup({ skippedOtp } = {}) {
    completingRef.current = true;
    const me = await fetchMe();
    if (skippedOtp || !otpRequired) {
      setUser(me, false);
    }
    nav("/markets", { replace: true, state: { justRegistered: true } });
  }

  async function cancelSignup() {
    if (!window.confirm("This will delete your in-progress signup so you can start over with a different email. Continue?")) return;
    try { await api.post("/api/auth/cancel-signup"); } catch { /* force clean state */ }
    await fetchMe();
    setStep(1);
  }

  const steps = buildSteps(otpRequired);
  const mobileStep = 3;

  return (
    <AuthPageLayout
      layoutTestId="register-layout"
      showBack
      navPrompt="Already have an account?"
      navLinkLabel="Log in"
      navLinkTo="/login"
      navLinkTestId="register-nav-login"
    >
      {showVerifyLater && (
        <VerifyLaterPopup
          onContinue={async () => {
            setShowVerifyLater(false);
            await enterAppAfterSignup({ skippedOtp: true });
          }}
        />
      )}

      <div
        className="rounded-[20px] border border-[var(--cm-border)] bg-white shadow-[0_32px_80px_-40px_rgba(29,45,63,0.2)] overflow-hidden grid lg:grid-cols-[1fr_380px]"
        data-testid="register-card"
      >
        <div className="p-8 lg:p-10">
          <h1 className="text-[32px] lg:text-[36px] font-extrabold text-cm-navy tracking-tight">
            Sign up
          </h1>
          <p className="mt-2 text-[14px] text-cm-grey">
            Open your CoinMBX account in minutes. Verify your email
            {otpRequired ? " and mobile" : ""} to start exploring — KYC unlocks trading.
          </p>

          <div className="mt-6">
            <Stepper current={step} steps={steps} />
          </div>

          {step === 1 && (
            <Step1Account
              onDone={async (payload) => {
                const otp = payload.otpRequired !== false;
                setOtpRequired(otp);
                if (payload?.devOtp) setDevEmailOtp(payload.devOtp);
                await refreshThen(2);
              }}
            />
          )}
          {step === 2 && (
            <Step2Email
              email={pendingUser?.email}
              initialDevOtp={devEmailOtp}
              onDevOtp={setDevEmailOtp}
              onDone={() => refreshThen(3)}
              onCancelSignup={cancelSignup}
            />
          )}
          {step === mobileStep && (
            <Step3Mobile
              otpRequired={otpRequired}
              onDone={async ({ skippedOtp }) => {
                if (skippedOtp) {
                  completingRef.current = true;
                  const me = await fetchMe();
                  setUser(me, false);
                  setShowVerifyLater(true);
                } else {
                  await enterAppAfterSignup({ skippedOtp: false });
                }
              }}
            />
          )}

          <p className="mt-8 text-[11px] text-cm-grey leading-relaxed border-t border-[var(--cm-border)] pt-6">
            CoinMBX complies with applicable Indian regulations for virtual digital assets. By
            signing up you agree to our{" "}
            <Link to="/terms" className="text-cm-blue font-semibold hover:underline">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-cm-blue font-semibold hover:underline">
              Privacy Policy
            </Link>
            .
          </p>

          <div
            className="lg:hidden mt-8 rounded-[14px] border border-[var(--cm-border)] bg-cm-lavender/30 p-5"
            data-testid="register-download-mobile"
          >
            <p className="text-[13px] font-bold text-cm-navy mb-3">Download app</p>
            <p className="text-[12px] text-cm-grey mb-4">
              Get the Android APK from our website — no app store required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center justify-center gap-2 w-full h-[44px] rounded-[12px] bg-cm-blue text-white font-bold text-[13px]"
            >
              Go to download page
            </Link>
          </div>
        </div>

        <div className="hidden lg:block">
          <LoginDownloadPanel />
        </div>
      </div>
    </AuthPageLayout>
  );
}
